import React, { useState } from "react";
import {
  IconButton,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { Feed } from "@mui/icons-material";
import { saveAs } from "file-saver";
import api from "../../api";
import { calculateStorieIndex } from "../../utils/storieUtils";
import {
  calculateAkkClass,
  getAkkDepthCode,
  getAkkTextureCode,
  getAkkSlopeCode,
  getLandUseCode,
} from "../../utils/akkUtils";
import { fetchAllSondasByJob } from "../../utils/sondaFetch";
import { plainValue } from "../../utils/valueUtils";

/**
 * SONDA İZAH CETVELİ (PDF)
 *
 * Biçim kurumun güncel "SONDA İZAH CETVELİ" belgesinden alınmıştır.
 * Her sonda TEK bir çerçeve içinde basılır:
 *
 *   +---------------------------+----------------------------------+
 *   | Örnek Sonda Numarası S1   |      X Değeri            381737  |
 *   | Seri Adı         NAİPLER  |      Y Değeri           4385479  |
 *   | ...                       |      ...                         |
 *   | Eğim                6-12  |                                  |
 *   +---------------------------------------+----------------------+
 *   | Derinlik | Bunye | Kireç | Tuz | pH   | Kont_no   Y      X   |
 *   |   0-15   |   L   | 0,574 | ... | 6,99 | 35052  4384961 381471|
 *   +---------------------------------------+----------------------+
 *
 * Koordinatlar en saga yaslidir; AKK ve Indeks formulleri tek satirda kalir;
 * kontrol sondalari SAG ALTTA, katman tablosunun yanindadir.
 *
 * HTML burada kurulur; PDF'e çevirme işini sunucudaki puppeteer yapar
 * (POST /admin/sonda-izah-pdf) - tarayıcıya ek kütüphane yüklenmez.
 */
export default function JobTSondaExportButton({ jobId, jobName }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      // Sonda ucu sayfalidir; fetchAllSondasByJob butun sayfalari dolasir.
      const [allJobSondas, profileRes] = await Promise.all([
        fetchAllSondasByJob(jobId),
        api.get(`/admin/job-profiles/${jobId}`),
      ]);

      const jobSondas = allJobSondas.map((s) => {
        let fullData = {};
        try {
          fullData =
            typeof s.full_data === "string"
              ? JSON.parse(s.full_data)
              : s.full_data;
        } catch (e) {}
        return { ...s, ...fullData };
      });

      if (jobSondas.length === 0) {
        alert("Bu çalışmaya ait sonda verisi bulunamadı.");
        setLoading(false);
        return;
      }

      const jobProfiles = profileRes.data.success ? profileRes.data.data : [];
      const parsedProfiles = jobProfiles.map((p) => {
        let fullData = {};
        try {
          fullData =
            typeof p.full_data === "string"
              ? JSON.parse(p.full_data)
              : p.full_data;
        } catch (e) {}
        return { ...p, ...fullData };
      });

      const anaSondalar = jobSondas.filter(
        (s) => s.sonda_tipi === "Örnek" || s.sonda_tipi === "İstikşafi",
      );
      const kontrolSondalar = jobSondas.filter(
        (s) => s.sonda_tipi === "Kontrol",
      );

      anaSondalar.sort((a, b) => {
        const numA = parseInt((a.sonda_no || "0").replace(/\D/g, "")) || 0;
        const numB = parseInt((b.sonda_no || "0").replace(/\D/g, "")) || 0;
        return numA - numB;
      });

      const normNo = (v) =>
        String(v ?? "")
          .trim()
          .toLocaleUpperCase("tr-TR");
      const anaNoSeti = new Set(anaSondalar.map((s) => normNo(s.sonda_no)));
      const bagsizKontroller = kontrolSondalar.filter(
        (k) => !anaNoSeti.has(normNo(k.benzer_sonda_no)),
      );

      const esc = (v) =>
        v === null || v === undefined
          ? ""
          : String(v)
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;");

      // Yan faktorler BİTİŞİK yazilir: "T2R0K".
      // Veri bicimleri: "Ç1 - Hafif Çakıllı(%10>)", "R0 - Az Kayalı",
      // "Sulanan Tarım Arazisi (Sy)". "Yok"/"Taşsız" gibi kodsuzlar atlanir.
      const yanFaktorler = (kaynak) => {
        const kod = (v) => {
          if (!v) return "";
          const metin = String(v).trim();
          if (metin.includes(" - ")) return metin.split(" - ")[0].trim();
          const m = metin.match(/\(([^)]+)\)/);
          if (m) return m[1].trim();
          return "";
        };
        return [
          kod(kaynak.cakillilik),
          kod(kaynak.taslilik),
          kod(kaynak.kayalilik),
          getLandUseCode(kaynak.arazi_kullanim_sinif),
        ]
          .filter(Boolean)
          .join("");
      };

      // Seri-Faz Haritalama Birimi: seri kodu + egim sinifi + fazlar.
      // Belgedeki aradaki tek rakam ("NP 5 C") henuz netlesmedigi icin yok.
      const seriFazBirimi = (kaynak) => {
        const fazKodu = (deger, harf) => {
          const m = String(deger || "").match(
            new RegExp(harf + "\\s*(\\d)", "i"),
          );
          return m ? harf.toLowerCase() + m[1] : "";
        };
        return [
          kaynak.seri_kodu || "",
          getAkkSlopeCode(kaynak.egim),
          kaynak.derinlik ? `d${getAkkDepthCode(kaynak.derinlik)}` : "",
          fazKodu(kaynak.taslilik, "T"),
          fazKodu(kaynak.kayalilik, "R"),
        ]
          .filter(Boolean)
          .join(" ");
      };

      const bloklar = [];

      anaSondalar.forEach((s, sondaIndex) => {
        // Belgedeki gibi duz sira numarasi: 1, 2, 3...
        const siraKodu = String(sondaIndex + 1);

        // Sondadaki benzer_profil duz sayi ("596"), profildeki profil_no ise
        // "P" onekli ("P596") kaydediliyor. Duz karsilastirmada 504 sondanin
        // yalnizca 4'u eslesiyordu; bu yuzden profil grubu bos kaliyor,
        // A faktoru varsayilana dusuyor ve INDEKS yanlis cikiyordu.
        // Iki tarafi da rakama indirgeyerek eslestiriyoruz.
        const profilAnahtar = (v) => String(v ?? "").replace(/\D/g, "");
        const aranan = profilAnahtar(s.benzer_profil);
        const baseProfile =
          (aranan &&
            parsedProfiles.find(
              (p) => profilAnahtar(p.profil_no) === aranan,
            )) ||
          {};

        // Profil ve sonda bazi alanlari farkli isimle tutuyor
        // (profilde tesirli_derinlik, sondada derinlik).
        const ESANLAMLI = {
          derinlik: ["derinlik", "tesirli_derinlik"],
          egim: ["egim", "egim_yuzde"],
          drenaj: ["drenaj", "tabii_drenaj"],
          su_erozyonu: ["su_erozyonu", "su_erozyon_derecesi"],
        };
        const al = (key) => {
          const adaylar = ESANLAMLI[key] || [key];
          for (const k of adaylar) {
            const own = s[k];
            if (own !== undefined && own !== null && own !== "")
              return plainValue(own);
          }
          for (const k of adaylar) {
            const pv = baseProfile[k];
            if (pv !== undefined && pv !== null && pv !== "")
              return plainValue(pv);
          }
          return "";
        };

        const horizons =
          s.horizons && s.horizons.length > 0
            ? s.horizons
            : baseProfile.horizons || [];

        const hesapVeri = {
          profilgrup: baseProfile.profilgrup || "",
          tesirli_derinlik: al("derinlik"),
          egim_yuzde: al("egim"),
          tabii_drenaj: al("drenaj"),
          su_erozyon_derecesi: al("su_erozyonu"),
          tuzluluk: al("tuzluluk"),
          alkalilik: al("alkalilik"),
          taslilik: al("taslilik"),
          cakillilik: al("cakillilik"),
          kayalilik: al("kayalilik"),
          arazi_kullanim_sinif: al("arazi_kullanim_sinif"),
          sel_basmasi: al("sel_basmasi"),
          seri_kodu: baseProfile.seri_kodu || "",
          derinlik: al("derinlik"),
          egim: al("egim"),
          horizons,
        };

        let akkSinif = "";
        let altTahdit = "";
        let akkFormul = "";
        let gecirgenlik = "";
        try {
          const akk = calculateAkkClass(hesapVeri);
          akkSinif = akk.finalClass || "";
          altTahdit = akk.subClassString || "";
          gecirgenlik =
            akk.formulaPermeability && akk.formulaPermeability !== 4
              ? String(akk.formulaPermeability)
              : "";
          akkFormul = `[(${akk.numerator})/(${akk.denominator})]${yanFaktorler(hesapVeri)}`;
        } catch (e) {}

        let indeksDeger = "";
        try {
          const st = calculateStorieIndex(hesapVeri);
          indeksDeger =
            st.finalIndex === "NaN"
              ? ""
              : String(st.finalIndex).replace(".", ",");
        } catch (e) {}

        const derinlikKodu = getAkkDepthCode(al("derinlik"));
        const ustBunye = getAkkTextureCode(
          horizons?.[0]?.tekstur,
          horizons?.[0]?.kirec,
        ).replace(/[0-9]/g, "");
        const altBunye = horizons?.[1]?.tekstur
          ? getAkkTextureCode(horizons[1].tekstur, horizons[1].kirec).replace(
              /[0-9]/g,
              "",
            )
          : ustBunye;
        const egimIndeks = (() => {
          const kod = getAkkSlopeCode(al("egim"));
          const harf = kod.charAt(0);
          const rakam = kod.slice(1);
          return rakam ? `${rakam}${harf}` : harf;
        })();
        const erozyon = al("su_erozyonu")
          ? String(al("su_erozyonu")).charAt(0)
          : "1";
        const indeksFormul =
          `${baseProfile.profilgrup || ""}[(${derinlikKodu}${ustBunye}${gecirgenlik}${altBunye})/` +
          `(${egimIndeks}-${erozyon})]${yanFaktorler(hesapVeri)}`;

        // --- ÜST BLOK ---
        const satirlar = [
          ["Örnek Sonda Numarası", siraKodu, "X Değeri", s.koordinat_x || ""],
          [
            "Seri Adı",
            baseProfile.toprak_serisi || "",
            "Y Değeri",
            s.koordinat_y || "",
          ],
          [
            "Seri-Faz Haritalama Birim",
            seriFazBirimi(hesapVeri),
            "Derinlik (cm)",
            al("derinlik"),
          ],
          ["Ana Materyal", al("ana_materyal"), "A.K.K Sınıfı", akkSinif],
          ["BTG", al("btg"), "Alt Tahdit", altTahdit],
          ["AKK Haritalama Birimi", akkFormul, "İndeks", indeksDeger],
          [
            "İndeks Haritalama Birimi",
            indeksFormul,
            "Arazi Kul.",
            getLandUseCode(al("arazi_kullanim_sinif")),
          ],
          ["Eğim", al("egim"), "", ""],
        ];

        // Formul satirlari tek satirda kalmali (nowrap).
        const formulSatiri = (e) =>
          e === "AKK Haritalama Birimi" || e === "İndeks Haritalama Birimi";

        const ustSatirlarHtml = satirlar
          .map(
            ([se, sd, sae, sad]) => `
              <tr>
                <td class="et">${esc(se)}</td>
                <td class="dg${formulSatiri(se) ? " formul" : ""}">${esc(sd)}</td>
                <td class="et2">${esc(sae)}</td>
                <td class="dg2">${esc(sad)}</td>
              </tr>`,
          )
          .join("");

        // --- KONTROL SONDALARI (sağ sütun) ---
        const kSondalar = kontrolSondalar
          .filter((k) => normNo(k.benzer_sonda_no) === normNo(s.sonda_no))
          .sort((a, b) => {
            const na = parseInt((a.sonda_no || "0").replace(/\D/g, "")) || 0;
            const nb = parseInt((b.sonda_no || "0").replace(/\D/g, "")) || 0;
            return na - nb;
          });

        const kontrolHtml =
          kSondalar.length > 0
            ? `<table class="kont">
                 <tr><th>Kont_no</th><th>Y</th><th>X</th></tr>
                 ${kSondalar
                   .map(
                     (k) => `<tr>
                       <td class="ort">${esc(k.sonda_no)}</td>
                       <td class="ort">${esc(k.koordinat_y)}</td>
                       <td class="ort">${esc(k.koordinat_x)}</td>
                     </tr>`,
                   )
                   .join("")}
               </table>`
            : "";

        // --- KATMAN TABLOSU ---
        const katmanHtml = `
          <table class="katman">
            <tr>
              <th>Derinlik (cm)</th><th>Bunye</th><th>Kireç</th>
              <th>Toplamtuz</th><th>pH</th><th>Bor ppm</th>
            </tr>
            ${
              (horizons || []).length > 0
                ? horizons
                    .map(
                      (h) => `<tr>
                        <td class="ort">${esc(`${h.derinlik_min ?? 0}-${h.derinlik_max ?? "?"}`)}</td>
                        <td class="ort">${esc(h.tekstur ? String(h.tekstur).split(" ")[0] : "")}</td>
                        <td class="ort">${esc(h.lab_raw_kirec !== undefined ? h.lab_raw_kirec : plainValue(h.kirec))}</td>
                        <td class="ort">${esc(h.lab_raw_ec !== undefined ? h.lab_raw_ec : "")}</td>
                        <td class="ort">${esc(h.ph !== undefined ? h.ph : "")}</td>
                        <td class="ort">${esc(h.lab_raw_bor !== undefined ? h.lab_raw_bor : "")}</td>
                      </tr>`,
                    )
                    .join("")
                : `<tr><td colspan="6" class="ort bos">Katman verisi yok</td></tr>`
            }
          </table>`;

        // Her sonda TEK cerceve icinde, sayfa ortasindan bolunmez.
        // Belgedeki gibi: ust blok tam genislik, ALTTA solda katman tablosu
        // sagda o sondaya bagli kontrol sondalari.
        bloklar.push(`
          <div class="sonda">
            <table class="ust">${ustSatirlarHtml}</table>
            <table class="alt">
              <tr>
                <td class="katmanHucre">${katmanHtml}</td>
                <td class="kontHucre">${kontrolHtml}</td>
              </tr>
            </table>
          </div>`);
      });

      const bagsizHtml =
        bagsizKontroller.length > 0
          ? `<div class="sonda">
               <div class="uyari">BAĞLANTISIZ KONTROL SONDALARI</div>
               <table class="katman">
                 <tr><th>Sonda No</th><th>Bağlı Olduğu</th><th>Y</th><th>X</th></tr>
                 ${bagsizKontroller
                   .map(
                     (k) => `<tr>
                       <td class="ort">${esc(k.sonda_no)}</td>
                       <td class="ort">${esc(k.benzer_sonda_no || "—")}</td>
                       <td class="ort">${esc(k.koordinat_y)}</td>
                       <td class="ort">${esc(k.koordinat_x)}</td>
                     </tr>`,
                   )
                   .join("")}
               </table>
             </div>`
          : "";

      const html = `<!doctype html>
<html lang="tr"><head><meta charset="utf-8"><title>Sonda İzah Cetveli</title>
<style>
  * { font-family: Arial, sans-serif; box-sizing: border-box; }
  body { margin: 0; font-size: 8.5pt; color: #000; }
  .baslik { text-align: center; font-weight: bold; font-size: 10pt;
            margin-bottom: 10px; line-height: 1.35; }
  .sonda { border: 1.4pt solid #000; margin-bottom: 10px;
           page-break-inside: avoid; break-inside: avoid; }
  table { border-collapse: collapse; width: 100%; table-layout: fixed; }

  /* Ust blok: sol etiket/deger + SAGA YASLI koordinat sutunlari */
  .ust td { border: 0.5pt solid #000; padding: 2px 4px;
            overflow: hidden; text-overflow: ellipsis; }
  .ust .et  { background: #f2f2f2; font-weight: bold; width: 33%;
              white-space: nowrap; }
  .ust .dg  { width: 45%; }
  .ust .et2 { background: #f2f2f2; font-weight: bold; width: 16%;
              white-space: nowrap; }
  .ust .dg2 { width: 18%; text-align: right; white-space: nowrap; }

  /* AKK / Indeks formulleri tek satirda kalir */
  .ust .formul { white-space: nowrap; letter-spacing: -0.1pt; }

  /* Alt blok: solda katman tablosu, sagda bagli kontrol sondalari */
  .alt > tbody > tr > td { vertical-align: top; padding: 0; border: none; }
  .katmanHucre { width: 72%; }
  .kontHucre   { width: 28%; border-left: 1pt solid #000; }
  .ort { text-align: center; }
  .katman { border-top: 1pt solid #000; }
  .katman th, .kont th { background: #e8e8e8; border: 0.5pt solid #000;
                         padding: 2px 4px; font-size: 8pt; }
  .katman td, .kont td { border: 0.5pt solid #000; padding: 2px 4px; }
  .bos { font-style: italic; color: #666; }
  .uyari { background: #fdecea; color: #b3261e; font-weight: bold;
           padding: 3px 6px; border-bottom: 1pt solid #000; }
</style></head><body>
  <div class="baslik">${esc(jobName || "")} — SONDA İZAH CETVELİ</div>
  ${bloklar.join("")}
  ${bagsizHtml}
</body></html>`;

      // PDF'e cevirme islemi sunucuda (puppeteer) yapiliyor.
      const res = await api.post(
        "/admin/sonda-izah-pdf",
        { html, jobName },
        { responseType: "blob" },
      );

      saveAs(
        new Blob([res.data], { type: "application/pdf" }),
        `${jobName}_Sonda_Izah_Cetveli.pdf`,
      );
    } catch (error) {
      console.error("Sonda İzah Cetveli hatası:", error);
      alert(
        "Cetvel oluşturulurken bir hata meydana geldi. Sunucuda PDF desteği kurulu olmayabilir.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title="Sonda İzah Cetveli (PDF) İndir">
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <IconButton
          color="success"
          onClick={handleExport}
          disabled={loading}
          sx={{
            bgcolor: alpha(theme.palette.success.main, 0.05),
            mb: 0.5,
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="success" />
          ) : (
            <Feed fontSize="small" />
          )}
        </IconButton>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.6rem",
            color: "text.secondary",
            fontWeight: "bold",
            textAlign: "center",
            lineHeight: 1.1,
          }}
        >
          SONDA İZAH
          <br />
          CETVELİ
        </Typography>
      </Box>
    </Tooltip>
  );
}
